import { API_BASE } from './client.js'
import { ApiError } from '../types/api.js'

function authHeaders(): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' }
  const t = localStorage.getItem('mf_access')
  if (t) h['Authorization'] = `Bearer ${t}`
  return h
}

async function toApiError(res: Response, fallback: string): Promise<ApiError> {
  let body: unknown = null
  try { body = await res.json() } catch {}
  const err = (body as any)?.error ?? { code: 'UNKNOWN', message: fallback }
  return new ApiError(res.status, err)
}

export interface DeliveryZoneResponse {
  id: string
  locationId: string
  name: string
  geometry: { type: 'Polygon'; coordinates: number[][][] }
  price: number
  minOrderAmount: number
  freeDeliveryThreshold: number | null
  eta: number | null
  priority: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface CreateDeliveryZoneData {
  name: string
  geometry: { type: 'Polygon'; coordinates: number[][][] }
  price?: number
  minOrderAmount?: number
  freeDeliveryThreshold?: number
  eta?: number
  priority?: number
}

export interface UpdateDeliveryZoneData {
  name?: string
  geometry?: { type: 'Polygon'; coordinates: number[][][] }
  price?: number
  minOrderAmount?: number
  freeDeliveryThreshold?: number | null
  eta?: number
  priority?: number
  isActive?: boolean
}

function base(orgId: string, locId: string): string {
  return `${API_BASE}/organizations/${orgId}/locations/${locId}/delivery-zones`
}

export async function getDeliveryZones(orgId: string, locId: string): Promise<DeliveryZoneResponse[]> {
  const res = await fetch(base(orgId, locId), { headers: authHeaders() })
  if (!res.ok) throw await toApiError(res, 'Failed to load delivery zones')
  const b = await res.json()
  return b.data
}

export async function getDeliveryZone(orgId: string, locId: string, zoneId: string): Promise<DeliveryZoneResponse> {
  const res = await fetch(`${base(orgId, locId)}/${zoneId}`, { headers: authHeaders() })
  if (!res.ok) throw await toApiError(res, 'Failed to load delivery zone')
  const b = await res.json()
  return b.data
}

export async function createDeliveryZone(orgId: string, locId: string, data: CreateDeliveryZoneData): Promise<DeliveryZoneResponse> {
  const res = await fetch(base(orgId, locId), {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to create delivery zone')
  const b = await res.json()
  return b.data
}

export async function updateDeliveryZone(orgId: string, locId: string, zoneId: string, data: UpdateDeliveryZoneData): Promise<DeliveryZoneResponse> {
  const res = await fetch(`${base(orgId, locId)}/${zoneId}`, {
    method: 'PATCH',
    headers: authHeaders(),
    body: JSON.stringify(data),
  })
  if (!res.ok) throw await toApiError(res, 'Failed to update delivery zone')
  const b = await res.json()
  return b.data
}

export async function deleteDeliveryZone(orgId: string, locId: string, zoneId: string): Promise<void> {
  const res = await fetch(`${base(orgId, locId)}/${zoneId}`, { method: 'DELETE', headers: authHeaders() })
  if (!res.ok) throw await toApiError(res, 'Failed to delete delivery zone')
}